import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, Phone, Mail, MapPin, ChevronRight } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import CartDrawer from '@/components/CartDrawer';

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { cartCount, isOpen, setIsOpen } = useCart();
  const location = useLocation();

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/shop', label: 'Shop' },
    { href: '/cabinets', label: 'Cabinets' },
    { href: '/about', label: 'About' },
    { href: '/quote', label: 'Free Quote' },
    { href: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Top bar */}
      <div className="bg-[#1A1A1A] text-white py-2 text-sm hidden md:block">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center gap-6">
            <a href="tel:+1-647-550-1313" className="flex items-center gap-2 hover:text-[#8B5E3C] transition-colors">
              <Phone size={14} />
              <span>+1-647-550-1313</span>
            </a>
            <a href="mailto:kitchen99.ca@gmail.com" className="flex items-center gap-2 hover:text-[#8B5E3C] transition-colors">
              <Mail size={14} />
              <span>kitchen99.ca@gmail.com</span>
            </a>
          </div>
          <div>
            <span>Free Shipping on Orders Over $2,500</span>
          </div>
        </div>
      </div>

      {/* Main header */}
      <header className="sticky top-0 z-50 glass border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <span className="font-manrope text-2xl font-extrabold tracking-tight text-[#1A1A1A]">
                Kitchen<span className="text-[#8B5E3C]">99.ca</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`nav-link link-underline ${isActive(link.href) ? 'text-[#8B5E3C]' : ''}`}
                  data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsOpen(true)}
                className="relative p-2 hover:bg-[#F9F7F2] rounded-md transition-colors"
                data-testid="cart-button"
              >
                <ShoppingCart size={24} className="text-[#1A1A1A]" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#8B5E3C] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">
                    {cartCount}
                  </span>
                )}
              </button>

              {/* Mobile menu button */}
              <button
                className="md:hidden p-2 hover:bg-[#F9F7F2] rounded-md transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-button"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[#E5E7EB] bg-white" data-testid="mobile-nav">
            <nav className="flex flex-col py-4 px-6">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`py-3 border-b border-[#E5E7EB] text-[#1A1A1A] font-medium ${isActive(link.href) ? 'text-[#8B5E3C]' : ''}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </header>

      <CartDrawer open={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
};

export const Footer = () => {
  const quickLinks = [
    { href: '/shop', label: 'Shop Cabinets' },
    { href: '/quote', label: 'Free Quote' },
    { href: '/about', label: 'About Us' },
    { href: '/contact', label: 'Contact' },
  ];

  const categories = [
    { label: 'Base Cabinets' },
    { label: 'Wall Cabinets' },
    { label: 'Tall Cabinets' },
    { label: 'Specialty Cabinets' },
  ];

  return (
    <footer className="footer" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div>
            <h3 className="font-manrope text-2xl font-extrabold mb-4">
              Kitchen<span className="text-[#8B5E3C]">99.ca</span>
            </h3>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Premium kitchen cabinets built for your home. Quality craftsmanship, competitive pricing, and exceptional service.
            </p>
            <div className="flex flex-col gap-3">
              <a href="tel:+1-647-550-1313" className="flex items-center gap-2">
                <Phone size={16} className="text-[#8B5E3C]" />
                <span>+1-647-550-1313</span>
              </a>
              <a href="mailto:kitchen99.ca@gmail.com" className="flex items-center gap-2">
                <Mail size={16} className="text-[#8B5E3C]" />
                <span>kitchen99.ca@gmail.com</span>
              </a>
              <div className="flex items-start gap-2">
                <MapPin size={16} className="text-[#8B5E3C] mt-1" />
                <span>Toronto, ON, Canada</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-manrope font-semibold text-lg mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="flex items-center gap-2 group">
                    <ChevronRight size={14} className="text-[#8B5E3C] opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span>{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h4 className="font-manrope font-semibold text-lg mb-4">Categories</h4>
            <ul className="space-y-3">
              {categories.map((cat) => (
                <li key={cat.label}>
                  <Link to="/shop" className="flex items-center gap-2 group">
                    <ChevronRight size={14} className="text-[#8B5E3C] opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span>{cat.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-manrope font-semibold text-lg mb-4">Stay Updated</h4>
            <p className="text-gray-400 mb-4">
              Subscribe for exclusive deals and design inspiration.
            </p>
            <form className="flex flex-col gap-3">
              <input
                type="email"
                placeholder="Your email"
                className="bg-white/10 border border-white/20 px-4 py-3 text-white placeholder:text-gray-400 focus:outline-none focus:border-[#8B5E3C]"
              />
              <button
                type="submit"
                className="bg-[#8B5E3C] text-white px-4 py-3 font-medium hover:bg-[#7a5235] transition-colors"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">
            © {new Date().getFullYear()} Kitchen99.ca All rights reserved.
          </p>
          <div className="flex gap-6 text-sm text-gray-400">
            <a href="#" className="hover:text-white">Privacy Policy</a>
            <a href="#" className="hover:text-white">Terms of Service</a>
            <a href="#" className="hover:text-white">Shipping Info</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => {
  return (
    <div className="page-container">
      <Header />
      <main className="main-content">{children}</main>
      <Footer />
    </div>
  );
};

export default Layout;
